import { Box, IconButton, Stack, Tooltip, Divider } from '@mui/material'
import {
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  EventSeat as DeskIcon,
} from '@mui/icons-material'
import { FindPersonEmployee } from '../../../services/booking/types'
import Paragraph from '../../../shared/UI/Paragraph'
import theme from '../../../theme/theme'

const COLOR_PRIMARY = '#2CC5A0'
const COLOR_BG = '#FFFFFF'
const CONTAINER_BORDER = '2px solid #2CC5A0'

interface FindPersonDetailsProps {
  personName: string
  currentBooking: FindPersonEmployee
  currentIndex: number
  totalBookings: number
  onNavigate: (direction: 'prev' | 'next') => void
  formatTime: (time: string) => string
}

export function FindPersonDetails({
  personName,
  currentBooking,
  currentIndex,
  totalBookings,
  onNavigate,
  formatTime
}: FindPersonDetailsProps) {
  const hasMultipleBookings = totalBookings > 1

  return (
    <Box
      border={CONTAINER_BORDER}
      bgcolor={COLOR_BG}
      borderRadius={2}
      p={2}
      mb={2}
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Header with title and navigation */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        sx={{ flexWrap: 'nowrap' }}
      >
        <Paragraph
          sx={{
            fontSize: 16,
            whiteSpace: 'nowrap',
            marginRight: 1,
            flexShrink: 0
          }}
          weight="bold"
          color={COLOR_PRIMARY}
        >
          Found Desk
        </Paragraph>
        
        {hasMultipleBookings && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ flexShrink: 0 }}
          >
            <Paragraph
              sx={{
                fontSize: 12,
                whiteSpace: 'nowrap'
              }}
              color="#747474"
            >
              {currentIndex + 1}
              {' of '}
              {totalBookings}
            </Paragraph>
            <Box display="flex" sx={{ ml: 0.5 }}>
              <IconButton
                onClick={() => onNavigate('prev')}
                disabled={currentIndex === 0}
                size="small"
                sx={{ padding: '4px' }}
              >
                <Tooltip title="Previous" placement="top">
                  <ChevronLeftIcon
                    sx={{
                      fontSize: 18,
                      color: currentIndex === 0 ? '#ccc' : theme.palette.primary.main
                    }}
                  />
                </Tooltip>
              </IconButton>
              <IconButton
                onClick={() => onNavigate('next')}
                disabled={currentIndex >= totalBookings - 1}
                size="small"
                sx={{ padding: '4px' }}
              >
                <Tooltip title="Next" placement="top">
                  <ChevronRightIcon
                    sx={{
                      fontSize: 18,
                      color: currentIndex >= totalBookings - 1 ? '#ccc' : theme.palette.primary.main
                    }}
                  />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Details - Stacked vertically */}
      <Stack spacing={1.5}>
        {/* Display Name */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonIcon sx={{ fontSize: 18, color: '#747474', flexShrink: 0 }} />
          <Paragraph
            sx={{
              fontSize: 14,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            color="#2c2965"
            weight="medium"
          >
            {personName}
          </Paragraph>
        </Stack>

        {/* Location & Floor Plan */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocationOnIcon sx={{ fontSize: 18, color: '#747474', flexShrink: 0 }} />
          <Paragraph
            sx={{
              fontSize: 14,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            color="#2c2965"
          >
            {currentBooking.locationName}
            {', '}
            {currentBooking.floorPlanName}
          </Paragraph>
        </Stack>

        {/* Desk */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <DeskIcon sx={{ fontSize: 18, color: '#747474', flexShrink: 0 }} />
          <Paragraph sx={{ fontSize: 14 }} color="#2c2965">
            {'Desk '}
            {currentBooking.deskName}
          </Paragraph>
        </Stack>


        {/* Time */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccessTimeIcon sx={{ fontSize: 18, color: '#747474', flexShrink: 0 }} />
          <Paragraph sx={{ fontSize: 14 }} color="#2c2965">
            {formatTime(currentBooking.startTime)}
            {' - '}
            {formatTime(currentBooking.endTime)}
          </Paragraph>
        </Stack>
      </Stack>
    </Box>
  )
}